import React, { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import T from 'prop-types';
import InfiniteScroll from 'react-infinite-scroll-component';

import {
  Menu,
  MenuItem,
  MenuGroup,
  MenuButton,
  useMenuProps,
} from '../../DropdownSet';
import Button from '../../Buttons/Button';
import LoadingSpinner from '../../LoadingSpinner';

import api from '../api';
import { format } from '../../../utils/date';

const scrollableTarget = 'scrollable-target';
const infiniteScrollStyles = { overflow: 'visible' };

const SavedExportsDropdown = ({
  currentExport,
  onSelect,
}) => {
  const [options, setOptions] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [isError, setError] = useState(false);

  const currentPageRef = useRef(0);
  const hasMoreRef = useRef(false);
  const menuGroupRef = useRef();

  const menuProps = useMenuProps({
    align: 'start',
    menuStyle: { width: 268 },
    position: 'initial',
    overflow: 'visible',
    className: 'saved-exports-dropdown-menu',
  });

  const fetchCustomExports = useCallback(() => {
    setError(false);
    setLoading(true);

    api.fetchSavedCustomExports(currentPageRef.current)
      .then(({ data: { assets: { reports } } }) => {
        setLoading(false);

        hasMoreRef.current = reports.filters.pagination.total > reports.list.length;
        const list = reports.list.filter(report => report.id !== currentExport.id);
        setOptions(prevState => prevState.concat(list));
      }).catch(() => {
        setLoading(false);
        setError(true);
      });
  }, []);

  useEffect(() => {
    fetchCustomExports();
  }, []);

  const handleShowMore = useCallback(() => {
    currentPageRef.current += 1;
    fetchCustomExports();
  }, []);

  // Scroll up on open Menu
  const handleMenuChange = ({ open }) => {
    if (open && menuGroupRef.current?.scrollTop > 0) {
      menuGroupRef.current.scrollTop = 0;
    }
  };

  const renderServiceState = () => {
    if (isError) {
      return (
        <div className="saved-exports-service-box saved-exports-error">
          <p>An error occurred!</p>
          <Button size="sm" onClick={fetchCustomExports}>Try again</Button>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="saved-exports-service-box">
          <LoadingSpinner id="saved-custom-exports" className="text-blue" />
        </div>
      );
    }

    return (
      <div className="saved-exports-service-box">
        Your saved exports<br />will appear here.
      </div>
    );
  };

  return (
    <div className="saved-exports-dropdown-container">
      <Menu
        menuButton={(
          <MenuButton
            size="lg"
            variant="light"
          >
            Select Previous Settings
          </MenuButton>
        )}
        setDownOverflow
        {...menuProps}
        onMenuChange={handleMenuChange}
      >
        {!options.length ? renderServiceState() : (
          <Fragment>
            <header className="saved-exports-header-item">
              <p className="saved-exports-name">Name</p>
              <p className="saved-exports-created">Created</p>
            </header>
            <MenuGroup
              takeOverflow
              ref={menuGroupRef}
              id={scrollableTarget}
              style={{ maxHeight: 394, overflow: 'auto' }}
            >
              <InfiniteScroll
                dataLength={options.length}
                hasMore={hasMoreRef.current}
                scrollThreshold={0.95}
                scrollableTarget={scrollableTarget}
                style={infiniteScrollStyles}
                loader={(
                  <LoadingSpinner
                    size="sm"
                    id="saved-custom-exports-more"
                    className="text-blue justify-content-center"
                  />
                )}
                next={handleShowMore}
              >
                {options.map(option => (
                  <MenuItem key={option.id} onClick={() => onSelect(option)}>
                    <p className="saved-exports-name">{option.name}</p>
                    <p className="saved-exports-created">{format(option.created_at, 'MM/DD/YY')}</p>
                  </MenuItem>
                ))}
              </InfiniteScroll>
            </MenuGroup>
          </Fragment>
        )}
      </Menu>
    </div>
  );
};

SavedExportsDropdown.propTypes = {
  currentExport: T.object.isRequired,
  onSelect: T.func.isRequired,
};

export default SavedExportsDropdown;
